import React from "react";
import * as styles from "@styles/ClientsPage.css";
import "@styles/util/ClientsPage.global.css";
import EmptyView from "@view/EmptyView";
import * as Option from "@utils/Option";
import type { OptionTs } from "@ps-bridge/interop";

export type ClientsPageView = "documents" | "projects" | "clients";
export type ClientsPageActionPhase = "hidden" | "visible";
export type ClientsPageModalPhase = "hidden" | "visible";

export type ClientsPagePanelProps<DocumentItem, ProjectItem, ProjectMenu> = {
  activeView: ClientsPageView;
  activeClientName: string;
  activeProjectName: string;
  activeProjectLastEdited: string;
  hasActiveClient: boolean;
  headerSubtitleFallback: string;
  backToClientsButton: React.ReactElement;
  backToProjectsButton: React.ReactElement;
  panelTitleText: string;
  panelHintText: string;
  createProjectPhase: ClientsPageActionPhase;
  uploadPhase: ClientsPageActionPhase;
  addClientPhase: ClientsPageActionPhase;
  createProjectButton: React.ReactElement;
  uploadButton: React.ReactElement;
  addClientButton: React.ReactElement;
  panelBodyView: React.ReactElement;
  deleteTarget: OptionTs<DocumentItem>;
  deleteTargetView: (target: DocumentItem) => React.ReactElement;
  projectMenu: OptionTs<ProjectMenu>;
  projectMenuView: (menu: ProjectMenu) => React.ReactElement;
  renameTarget: OptionTs<ProjectItem>;
  renameTargetView: (target: ProjectItem) => React.ReactElement;
  deleteProjectTarget: OptionTs<ProjectItem>;
  deleteProjectTargetView: (target: ProjectItem) => React.ReactElement;
  createProjectModalPhase: ClientsPageModalPhase;
  createProjectModalView: React.ReactElement;
  createClientModalPhase: ClientsPageModalPhase;
  createClientModalView: React.ReactElement;
};

const ClientsPagePanel = <DocumentItem, ProjectItem, ProjectMenu>({
  activeView,
  activeClientName,
  activeProjectName,
  activeProjectLastEdited,
  hasActiveClient,
  headerSubtitleFallback,
  backToClientsButton,
  backToProjectsButton,
  panelTitleText,
  panelHintText,
  createProjectPhase,
  uploadPhase,
  addClientPhase,
  createProjectButton,
  uploadButton,
  addClientButton,
  panelBodyView,
  deleteTarget,
  deleteTargetView,
  projectMenu,
  projectMenuView,
  renameTarget,
  renameTargetView,
  deleteProjectTarget,
  deleteProjectTargetView,
  createProjectModalPhase,
  createProjectModalView,
  createClientModalPhase,
  createClientModalView,
}: ClientsPagePanelProps<DocumentItem, ProjectItem, ProjectMenu>) => {
  const documentsSubtitlePhase = hasActiveClient ? "active" : "empty";
  const documentsSubtitle = {
    active: (
      <div className={`${styles.subtitle} ${styles.projectSubtitle}`}>
        <div>Client: {activeClientName}</div>
        <div>Last edited {activeProjectLastEdited}</div>
      </div>
    ),
    empty: (
      <p className={`${styles.subtitle} ${styles.projectSubtitle}`}>
        {headerSubtitleFallback}
      </p>
    ),
  } as const satisfies Record<"active" | "empty", React.ReactElement>;

  const headerView = {
    documents: (
      <div className={styles.headerRow}>
        <div className={styles.headerTitleGroup}>
          <h1 className={styles.projectTitle}>{activeProjectName}</h1>
          {documentsSubtitle[documentsSubtitlePhase]}
        </div>
        <div className={styles.backColumn}>
          {backToClientsButton}
          {backToProjectsButton}
        </div>
      </div>
    ),
    projects: (
      <>
        <h1 className={styles.projectTitle}>
          {hasActiveClient ? activeClientName : "Projects"}
        </h1>
        <p className={styles.subtitle}>
          {hasActiveClient
            ? "Projects for this client."
            : "Select a client to see projects."}
        </p>
      </>
    ),
    clients: (
      <>
        <h1 className={styles.projectTitle}>Browse Clients</h1>
        <p className={styles.subtitle}>
          Organize work like a shared drive. Select a client to drill in.
        </p>
      </>
    ),
  } as const satisfies Record<ClientsPageView, React.ReactElement>;

  const createProjectView = {
    hidden: <EmptyView />,
    visible: createProjectButton,
  } as const satisfies Record<ClientsPageActionPhase, React.ReactElement>;

  const uploadView = {
    hidden: <EmptyView />,
    visible: uploadButton,
  } as const satisfies Record<ClientsPageActionPhase, React.ReactElement>;

  const addClientView = {
    hidden: <EmptyView />,
    visible: addClientButton,
  } as const satisfies Record<ClientsPageActionPhase, React.ReactElement>;

  const modalView = {
    hidden: <EmptyView />,
    visible: createProjectModalView,
  } as const satisfies Record<ClientsPageModalPhase, React.ReactElement>;

  const clientModalView = {
    hidden: <EmptyView />,
    visible: createClientModalView,
  } as const satisfies Record<ClientsPageModalPhase, React.ReactElement>;

  const deleteTargetRender = Option.fold(
    deleteTarget,
    () => <EmptyView />,
    (target) => deleteTargetView(target),
  );
  const projectMenuRender = Option.fold(
    projectMenu,
    () => <EmptyView />,
    (menu) => projectMenuView(menu),
  );
  const renameTargetRender = Option.fold(
    renameTarget,
    () => <EmptyView />,
    (target) => renameTargetView(target),
  );
  const deleteProjectTargetRender = Option.fold(
    deleteProjectTarget,
    () => <EmptyView />,
    (target) => deleteProjectTargetView(target),
  );

  return (
    <div
      className={styles.container}
      data-testid="page-clients"
      data-page-state="ready"
    >
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <p className={styles.kicker}>Workspace</p>
          {headerView[activeView]}
        </div>
      </header>

      <div className={styles.workspace}>
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <h2 className={styles.panelTitle}>{panelTitleText}</h2>
              <p className={styles.panelHint}>{panelHintText}</p>
            </div>
            {createProjectView[createProjectPhase]}
            {uploadView[uploadPhase]}
            {addClientView[addClientPhase]}
          </div>

          {panelBodyView}
        </section>
      </div>

      {deleteTargetRender}
      {projectMenuRender}
      {modalView[createProjectModalPhase]}
      {clientModalView[createClientModalPhase]}
      {renameTargetRender}
      {deleteProjectTargetRender}
    </div>
  );
};

export default ClientsPagePanel;
